import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getGroupMembers from '../GroupMemberList/getGroupMembers.api';
import getGroupMembersInMyGroup from '../GroupMemberList/getGroupMembersInMyGroup.api';
import Results from './Results';
import {useSnackbar} from 'notistack';
import {checkPermission} from '../../../components/Can/Can';
import updateGroupMember, {UpdateGroupMemberInput} from '../GroupMemberDetails/updateGroupMember.api';
import limitedUpdateGroupMember from '../GroupMemberDetails/limitedUpdateGroupMember.api';
import {formatGraphQlDate} from '../../../utils/formatters';
import anyLoading from '../../../utils/anyLoading';
import getMyGroupMembers from '../GroupMemberList/getMyGroupMembers.api';
import getMyTravellers from '../GroupMemberList/getMyTravellers.api';
import {checkIsAdmin} from '../../../utils/checkIsAdmin';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    results: {
        marginTop: theme.spacing(3)
    }
}));

// export interface FilterValues {
// }

export const GroupMembersPendingApprovalListContent = (props: {
    groupId?: number, groupRef?: string, isAdmin: boolean, canEdit: boolean, myGroupMembers?: boolean, showGroupColumn?: boolean
} ) => {
    const classes = useStyles();
    const {groupId, groupRef, isAdmin, canEdit, myGroupMembers, showGroupColumn} = props;
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [page, setPage] = useState(1);

    const {enqueueSnackbar} = useSnackbar();
    const canFullUpdate = checkPermission('groups.update');
    const [sendFullUpdateGroupMember, updateData] = updateGroupMember();
    const [sendLimitedUpdateGroupMember, limitedUpdateData] = limitedUpdateGroupMember();
    const sendUpdateGroupMember: any = canFullUpdate ? sendFullUpdateGroupMember : sendLimitedUpdateGroupMember;

    const hasRoomMember: WhereConstraints = {AND: []};
    if (!isAdmin) {
        hasRoomMember.AND!.push({
            column: 'END_DATE',
            operator: OPERATOR.GTE,
            value: formatGraphQlDate(moment())
        });
    }

    const addDefaultConstraints = (constraints: WhereConstraints) => {
        const whereConstraints = {...constraints}
        if (!whereConstraints.AND) whereConstraints.AND = [];
        if(groupId) whereConstraints.AND.push({column: 'group_id', operator: OPERATOR.EQ, value: groupId});
        whereConstraints.AND.push({column: 'cancelled', operator: OPERATOR.EQ, value: false});
        whereConstraints.AND.push({column: 'authorised', operator: OPERATOR.EQ, value: false});

        return whereConstraints;
    }

    const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);

    const [where, setWhere] = useState(addDefaultConstraints({}));


    const groupMembersData = myGroupMembers? getMyGroupMembers(rowsPerPage, page, where, orderBy, hasRoomMember):
        isAdmin ? getGroupMembers(rowsPerPage, page, where, orderBy, hasRoomMember) :
            groupRef ? getGroupMembersInMyGroup(groupRef!, rowsPerPage, page, where, orderBy, hasRoomMember) :
                getMyTravellers(rowsPerPage, page, where, orderBy, hasRoomMember);

    let groupMembers;
    if (groupMembersData.data && groupMembersData.data.groupMembers) groupMembers = groupMembersData.data.groupMembers;
    if (groupMembersData.data && groupMembersData.data.groupMembersInMyGroup) groupMembers = groupMembersData.data.groupMembersInMyGroup;
    if (groupMembersData.data && groupMembersData.data.myTravellers) groupMembers = groupMembersData.data.myTravellers;
    if (groupMembersData.data && groupMembersData.data.myGroupMembers) groupMembers = groupMembersData.data.myGroupMembers;

    const loadingData = anyLoading(groupMembersData, updateData, limitedUpdateData )

    const handleSearch = (value: string) => {
        let whereConstraints: WhereConstraints = {
            OR: [
                {column: 'first_name', operator: OPERATOR.LIKE, value: `%${value}%`},
                {column: 'last_name', operator: OPERATOR.LIKE, value: `%${value}%`},
                {column: 'email', operator: OPERATOR.LIKE, value: `%${value}%`},
            ]
        };

        whereConstraints = addDefaultConstraints(whereConstraints)

        setWhere(whereConstraints);
    };

    const handleUpdateMember = (memberId: number, authorised?: boolean, cancelled?: boolean) => {
        const sanitisedData: UpdateGroupMemberInput = {
            data: {
                id: memberId,
                authorised,
                cancelled,
            }
        };

        sendUpdateGroupMember({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Group Member Updated.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Group Member Update Failed!', {variant: 'error'});
            });
    }

    return (
        <React.Fragment>
            <LoadingOverlay loading={loadingData.loading}>

                <SearchBar className={classes.marginBottom} onSearch={handleSearch}/>

                {groupMembers && groupMembers.data && (
                    <Results
                        className={classes.results}
                        groupsMembers={groupMembers.data}
                        paginatorInfo={groupMembers.paginatorInfo}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        hideGroupColumn={!showGroupColumn}
                        isAdmin={isAdmin}
                        canEdit={canEdit}
                        groupRef={groupRef}
                        handleUpdateMember={handleUpdateMember}
                    />
                )}
            </LoadingOverlay>
        </React.Fragment>
    );
};

export const MyGroupMembersPendingApprovalList = () => {
    const classes = useStyles();
    const title = 'My Group Members Pending Approval';
    const isAdmin = checkIsAdmin();

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="users" title={title} />
            <GroupMembersPendingApprovalListContent myGroupMembers={true} isAdmin={isAdmin} canEdit={false} showGroupColumn={true}/>
        </Page>
    );
};

const GroupMembersPendingApprovalList = (props: { groupId: number, groupRef?: string, isAdmin: boolean, canEdit: boolean }) => {
    const classes = useStyles();
    const title = 'Group Members Pending Approval';

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="users" title={title} />
            <GroupMembersPendingApprovalListContent {...props}/>
        </Page>
    );
};

export default GroupMembersPendingApprovalList;
